import { useCallback } from 'react';
import { addBreadcrumb, setSentryContext } from '@/lib/sentry';
import * as Sentry from '@sentry/react';

/**
 * 페이지 성능 모니터링을 위한 커스텀 훅
 */
export const useSentryPerformance = () => {
  /**
   * API 호출 성능 추적
   */
  const trackApiCall = useCallback((endpoint: string, method: string = 'GET') => {
    addBreadcrumb({
      category: 'api',
      message: `API call: ${method} ${endpoint}`,
      level: 'info',
      data: {
        endpoint,
        method,
        timestamp: new Date().toISOString(),
      },
    });

    // 최신 Sentry API 사용
    return Sentry.startSpan(
      {
        name: `API: ${method} ${endpoint}`,
        op: 'http.client'
      },
      () => {
        // API 호출이 여기서 실행될 예정
      }
    );
  }, []);

  /**
   * 사용자 액션 추적
   */
  const trackUserAction = useCallback((action: string, data?: Record<string, any>) => {
    addBreadcrumb({
      category: 'user',
      message: `User action: ${action}`,
      level: 'info',
      data: {
        action,
        ...data,
        timestamp: new Date().toISOString(),
      },
    });
  }, []);

  /**
   * 에러 컨텍스트 추가
   */
  const addErrorContext = useCallback((context: Record<string, any>) => {
    setSentryContext('error_context', {
      ...context,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });
  }, []);

  return {
    trackApiCall,
    trackUserAction,
    addErrorContext,
  };
};

/**
 * React Query와 Sentry 통합을 위한 훅
 */
export const useSentryQueryIntegration = () => {
  const { trackApiCall, addErrorContext } = useSentryPerformance();

  /**
   * React Query에서 사용할 수 있는 onError 핸들러
   */
  const handleQueryError = useCallback((error: Error, variables?: any) => {
    addErrorContext({
      query_error: true,
      variables,
      error_type: error.constructor.name,
    });

    addBreadcrumb({
      category: 'query',
      message: `Query error: ${error.message}`,
      level: 'error',
      data: {
        error: error.message,
        variables,
      },
    });
  }, [addErrorContext]);

  /**
   * React Query에서 사용할 수 있는 mutation error 핸들러
   */
  const handleMutationError = useCallback((error: Error, variables?: any) => {
    addErrorContext({
      mutation_error: true,
      variables,
      error_type: error.constructor.name,
    });

    addBreadcrumb({
      category: 'mutation',
      message: `Mutation error: ${error.message}`,
      level: 'error',
      data: {
        error: error.message,
        variables,
      },
    });
  }, [addErrorContext]);

  return {
    trackApiCall,
    handleQueryError,
    handleMutationError,
  };
};